// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { GoogleOAuthProvider } from '@react-oauth/google';  // ← ADD THIS LINE
import ProfileCompletionPage from './components/ProfileCompletionModal';

import { 
  Home as HomeIcon,
  BarChart3, 
  TrendingUp, 
  User, 
  LogOut, 
  Key,
  X
} from 'lucide-react';
import './App.css';
import Home from './pages/Home';
import Submit from './pages/Submit';
import Login from './pages/Login';
import Analytics from './pages/Analytics';
import EmailVerification from './pages/EmailVerification'; 
import ResetPassword from './components/ResetPassword';
import VestingDetails from './pages/VestingDetails';
import Swap from './pages/Swap';
import Whitepaper from './pages/Whitepaper';

// 🚀 PERFORMANCE MONITORING COMPONENTS
const PerformanceDebugger = () => {
  const [fps, setFPS] = useState(60);
  const [memoryUsage, setMemoryUsage] = useState({});
  const [isVisible, setIsVisible] = useState(true);
  
  useEffect(() => {
    let frameCount = 0;
    let lastTime = performance.now();
    let animationId;
    
    // FPS Monitor
    const measureFPS = () => {
      frameCount++;
      const currentTime = performance.now();
      
      if (currentTime >= lastTime + 1000) {
        const currentFPS = Math.round((frameCount * 1000) / (currentTime - lastTime));
        setFPS(currentFPS);
        
        // Log performance issues
        if (currentFPS < 30) {
          console.warn(`🔴 Low FPS: ${currentFPS} - Performance bottleneck detected`);
          
          // Alert for critical performance issues
          if (currentFPS < 15) {
            console.error('🚨 CRITICAL: FPS below 15 - Major performance issue!');
          }
        }
        
        frameCount = 0;
        lastTime = currentTime;
      }
      
      animationId = requestAnimationFrame(measureFPS);
    };
    
    // Memory Monitor
    const measureMemory = () => {
      if ('memory' in performance) {
        const memInfo = performance.memory;
        const usagePercent = (memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit) * 100;
        
        setMemoryUsage({
          used: Math.round(memInfo.usedJSHeapSize / 1048576), // MB
          total: Math.round(memInfo.totalJSHeapSize / 1048576), // MB
          limit: Math.round(memInfo.jsHeapSizeLimit / 1048576), // MB
          percentage: usagePercent.toFixed(1)
        });
        
        // Warning for high memory usage
        if (usagePercent > 80) {
          console.warn(`🔴 High memory usage: ${usagePercent.toFixed(1)}% - Consider optimizing`);
        }
        
        if (usagePercent > 90) {
          console.error('🚨 CRITICAL: Memory usage above 90% - Risk of crashes!');
        }
      }
    };
    
    measureFPS();
    const memoryInterval = setInterval(measureMemory, 2000);
    
    return () => {
      cancelAnimationFrame(animationId);
      clearInterval(memoryInterval);
    };
  }, []);
  
  // Only show in development or when explicitly enabled
  if (process.env.NODE_ENV === 'production' && !localStorage.getItem('show_debug')) {
    return null;
  }
  
  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          background: 'rgba(0,0,0,0.5)',
          color: 'white',
          border: 'none',
          padding: '5px 10px',
          borderRadius: '3px',
          cursor: 'pointer',
          fontSize: '11px',
          zIndex: 10000
        }}
      >
        Show Debug
      </button>
    );
  }
  
  return (
    <div style={{
      position: 'fixed',
      top: '10px',
      right: '10px',
      background: 'rgba(0,0,0,0.9)',
      color: 'white',
      padding: '12px',
      borderRadius: '8px',
      fontSize: '11px',
      zIndex: 10000,
      fontFamily: 'monospace',
      minWidth: '200px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <strong>Performance Monitor</strong>
        <button
          onClick={() => setIsVisible(false)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '0'
          }}
        >
          ×
        </button>
      </div>
      
      <div style={{ marginBottom: '4px' }}>
        FPS: <span style={{
          color: fps < 30 ? '#ff4444' : fps < 45 ? '#ffaa44' : '#44ff44',
          fontWeight: 'bold'
        }}>{fps}</span>
      </div>
      
      {memoryUsage.used && (
        <>
          <div style={{ marginBottom: '4px' }}>
            Memory: <span style={{
              color: parseFloat(memoryUsage.percentage) > 80 ? '#ff4444' : '#44ff44'
            }}>{memoryUsage.used}MB</span> / {memoryUsage.total}MB
          </div>
          <div style={{ marginBottom: '4px' }}>
            Usage: <span style={{
              color: parseFloat(memoryUsage.percentage) > 80 ? '#ff4444' : '#44ff44'
            }}>{memoryUsage.percentage}%</span>
          </div>
          <div style={{ fontSize: '9px', color: '#aaa' }}>
            Limit: {memoryUsage.limit}MB
          </div>
        </>
      )}
      
      <div style={{ fontSize: '9px', color: '#aaa', marginTop: '8px' }}>
        Press F12 → Console for details
      </div>
    </div>
  );
};

// 🔍 SCROLL PERFORMANCE MONITOR
const ScrollPerformanceMonitor = () => {
  useEffect(() => {
    let scrollCount = 0;
    let lastScrollTime = performance.now();
    let longTaskCount = 0;
    
    const handleScroll = () => {
      const startTime = performance.now();
      scrollCount++;
      
      // Measure scroll handler execution time
      setTimeout(() => {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (duration > 16.67) { // Longer than 60fps frame
          longTaskCount++;
          console.warn(`🔴 Slow scroll handler: ${duration.toFixed(2)}ms`);
        }
      }, 0);
    };
    
    // Report scroll performance every 2 seconds
    const reportInterval = setInterval(() => {
      const currentTime = performance.now();
      const timeDiff = currentTime - lastScrollTime;
      
      if (timeDiff > 1000 && scrollCount > 0) {
        const scrollsPerSecond = (scrollCount * 1000) / timeDiff;
        
        console.log(`📊 Scroll Performance:`, {
          scrollsPerSecond: scrollsPerSecond.toFixed(1),
          longTasks: longTaskCount,
          totalScrolls: scrollCount
        });
        
        if (scrollsPerSecond > 60) {
          console.warn('🔴 High scroll frequency detected - consider throttling');
        }
        
        if (longTaskCount > 5) {
          console.warn('🔴 Multiple slow scroll handlers detected');
        }
        
        scrollCount = 0;
        longTaskCount = 0;
        lastScrollTime = currentTime;
      }
    }, 2000);
    
    // Throttled scroll listener
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', throttledScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      clearInterval(reportInterval);
    };
  }, []);
  
  return null;
};

// 🎯 LONG TASK MONITOR (for production)
const LongTaskMonitor = () => {
  useEffect(() => {
    let longTaskCount = 0;
    
    // Monitor long tasks (>50ms)
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.duration > 50) {
              longTaskCount++;
              console.warn(`🔴 Long task detected: ${entry.duration.toFixed(2)}ms`, {
                name: entry.name,
                startTime: entry.startTime,
                type: entry.entryType
              });
              
              // Critical performance alert
              if (entry.duration > 100) {
                console.error('🚨 CRITICAL: Task over 100ms - UI freeze risk!');
              }
            }
          });
        });
        
        observer.observe({ entryTypes: ['longtask'] });
        
        // Report long tasks every 30 seconds
        const reportInterval = setInterval(() => {
          if (longTaskCount > 0) {
            console.log(`📊 Long tasks in last 30s: ${longTaskCount}`);
            
            if (longTaskCount > 10) {
              console.error('🚨 Excessive long tasks - major performance issues detected!');
            }
            
            longTaskCount = 0;
          }
        }, 30000);
        
        return () => {
          observer.disconnect();
          clearInterval(reportInterval);
        };
      } catch (error) {
        console.warn('⚠️ PerformanceObserver not supported or failed to initialize');
      }
    }
  }, []);
  
  return null;
};

// Main App component that uses auth context
function AppContent() {
  const { isLoggedIn, logout, isLoading } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    window.location.href = '/login';
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const getNavLinkText = () => {
    return isLoggedIn ? 'My Account' : 'Login';
  };

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <Router>
      <div className="app">
        {/* 🚀 PERFORMANCE MONITORS */}
        <PerformanceDebugger />
        <ScrollPerformanceMonitor />
        <LongTaskMonitor />
        
        {/* Navigation Bar */}
        <nav className="navbar">
          <div className="nav-container">
            <Link to="/" className="nav-logo" onClick={closeMenu}>
              <img src="/tp1logo.jpg" alt="Trendphile" className="logo-image" />
            </Link>

            {/* Desktop Menu */}
            <ul className="nav-menu desktop-menu">
              <li className="nav-item">
                <Link to="/" className="nav-link">Home</Link>
              </li>
              <li className="nav-item">
                <Link to="/submit" className="nav-link">Submit</Link>
              </li>
              <li className="nav-item">
                <Link to="/analytics" className="nav-link">Analytics</Link>
              </li>
              {isLoggedIn ? (
                <>
                  <li className="nav-item">
                    <Link to="/login" className="nav-link">{getNavLinkText()}</Link>
                  </li>
                  <li className="nav-item">
                    <button className="nav-link logout-btn" onClick={handleLogout}>
                      Logout
                    </button>
                  </li>
                </>
              ) : (
                <li className="nav-item">
                  <Link to="/login" className="nav-link login-btn">{getNavLinkText()}</Link>
                </li>
              )}
            </ul>

            {/* Mobile Hamburger Button */}
            <button 
              className={`hamburger ${isMenuOpen ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>

          {/* Mobile Menu */}
          <div className={`mobile-menu ${isMenuOpen ? 'active' : ''}`}>
            <div className="mobile-menu-overlay" onClick={closeMenu}></div>
            <div className="mobile-menu-content">
              <div className="mobile-menu-header">
                <Link to="/" className="mobile-logo" onClick={closeMenu}>
                  <img src="/tp1logo.jpg" alt="Trendphile" />
                </Link>
                <button className="close-btn" onClick={closeMenu}>
                  <X size={24} />
                </button>
              </div>
              <ul className="mobile-nav-list">
                <li>
                  <Link to="/" className="mobile-nav-link" onClick={closeMenu}>
                    <HomeIcon size={20} className="nav-icon" />
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/submit" className="mobile-nav-link" onClick={closeMenu}>
                    <BarChart3 size={20} className="nav-icon" />
                    Submit
                  </Link>
                </li>
                <li>
                  <Link to="/analytics" className="mobile-nav-link" onClick={closeMenu}>
                    <TrendingUp size={20} className="nav-icon" />
                    Analytics
                  </Link>
                </li>
                {isLoggedIn ? (
                  <>
                    <li>
                      <Link to="/login" className="mobile-nav-link" onClick={closeMenu}>
                        <User size={20} className="nav-icon" />
                        My Account
                      </Link>
                    </li>
                    <li>
                      <button className="mobile-nav-link logout-link" onClick={handleLogout}>
                        <LogOut size={20} className="nav-icon" />
                        Logout
                      </button>
                    </li>
                  </>
                ) : (
                  <li>
                    <Link to="/login" className="mobile-nav-link login-link" onClick={closeMenu}>
                      <Key size={20} className="nav-icon" />
                      Login
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </nav>

        {/* Main Content Container */}
        <div className="main-content performance-optimized">
        <Routes>
  <Route path="/" element={<Home />} />
  <Route path="/submit" element={<Submit />} />
  <Route path="/login" element={<Login />} />
  <Route path="/signup" element={<Login />} />
  <Route path="/analytics" element={<Analytics />} />
  <Route path="/verify-email" element={<EmailVerification />} />
  <Route path="/reset-password" element={<ResetPassword />} />
  <Route path="/vesting-details" element={<VestingDetails />} />
  <Route path="/swap" element={<Swap />} />
  <Route path="/whitepaper" element={<Whitepaper />} />
  <Route 
    path="/profile-completion" 
    element={
      <ProfileCompletionPage 
        userToken={localStorage.getItem('token') || sessionStorage.getItem('token')} 
        onComplete={(rewardAmount) => {
          console.log('Profile completion reward earned:', rewardAmount);
        }}
      />
    } 
  />
</Routes>

        </div>
      </div>
    </Router>
  );
}

// Main App wrapper with AuthProvider and GoogleOAuthProvider
function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
